// utils/handleFormError.ts
import axios from "axios";
import toast from "react-hot-toast";
import { Path, UseFormSetError } from "react-hook-form";

/*
 * handleFormError
 *
 * Centralised error handler for every edit form in the dashboard.
 * Keeps individual form components free of repetitive axios/status logic.
 *
 * Behaviour by status:
 *   422 / 409  — field-level errors from the server are piped directly into
 *                react-hook-form via setError() so they appear inline next to
 *                the relevant field. Unknown fields fall back to a toast so
 *                nothing is silently swallowed.
 *   401        — not authenticated toast
 *   403        — not authorised toast
 *   anything else — generic message toast, falls back to status code if the
 *                   server did not return a message body
 *   non-axios  — catches unexpected JS errors (network down, JSON parse, etc.)
 *
 * Usage in any edit form:
 *
 *   } catch (err) {
 *       handleFormError(err, methods.setError, new Set(Object.keys(methods.getValues())));
 *   }
 */
export function handleFormError<T extends Record<string, unknown>>(
    err:             unknown,
    setError:        UseFormSetError<T>,
    validFieldNames: Set<string>,
): void {

    if (!axios.isAxiosError(err)) {
        toast.error("Something went wrong. Please try again.");
        return;
    }

    const status = err.response?.status;
    const body   = err.response?.data;

    // ── Field-level validation errors ─────────────────────────────────────
    if ((status === 422 || status === 409) && body?.errors) {
        Object.entries<string>(body.errors).forEach(([field, message]) => {
            if (validFieldNames.has(field)) {
                // Pipe into react-hook-form — renders inline under the field
                setError(field as Path<T>, { type: "server", message });
            } else {
                // Field the form doesn't know about — surface as toast
                toast.error(message);
            }
        });
        toast.error("Please fix the errors below and try again.");
        return;
    }

    // ── Auth errors ───────────────────────────────────────────────────────
    if (status === 401) {
        toast.error("You must be logged in to perform this action.");
        return;
    }

    if (status === 403) {
        toast.error("You do not have permission to perform this action.");
        return;
    }

    // ── Everything else (500, network, etc.) ──────────────────────────────
    toast.error(body?.message ?? `Unexpected error (${status}). Please try again.`);
}